import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CurrentTenantService } from '@suvo-bi-users';
import { ITextRowsTileInstance } from '../../../../interfaces/tiles/definitions/text-rows-tile-definition.interface';
import {
  resolveSiteAssetCssValue,
  resolveSiteAssetUrl,
} from '../../../../utils/resolve-site-asset-url.util';

@Component({
  standalone: false,
  selector: 'suvo-bi-text-rows-tile',
  templateUrl: './text-rows-tile.component.html',
  styleUrls: ['./text-rows-tile.component.scss'],
})
export class TextRowsTileComponent implements OnInit {
  private readonly navLogStorageKey = 'text-rows-tile:last-nav';

  @Input() tileInstance: ITextRowsTileInstance;

  constructor(
    private readonly tenantService: CurrentTenantService,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    this.logPendingNavigation();
  }

  private get siteBaseUrl(): string {
    return this.tenantService.currentTenantData?.primaryWebUrl ?? '';
  }

  get backgroundStyle(): string {
    return resolveSiteAssetCssValue(this.tileInstance?.background ?? '', this.siteBaseUrl);
  }

  getImgPath(path: string) {
    return resolveSiteAssetUrl(path, this.siteBaseUrl);
  }

  getRouteLink(routeLink?: string): string | null {
    if (!routeLink) {
      return null;
    }

    if (!routeLink.startsWith('/')) {
      return routeLink;
    }

    const currentLanguage = this.router.url.split('/').filter(Boolean)[0] || 'en';
    const firstSegment = routeLink.split('/').filter(Boolean)[0];

    // Keep absolute links that are already language-scoped.
    if (firstSegment === currentLanguage || /^[a-z]{2}(-[a-z]{2})?$/i.test(firstSegment)) {
      return routeLink;
    }

    return `/${currentLanguage}${routeLink}`;
  }

  logRouteNavigation(routeLink?: string) {
    const normalizedRoute = this.getRouteLink(routeLink);

    if (!normalizedRoute) {
      console.log('[TextRowsTile] Learn More link clicked with empty routeLink');
      return;
    }

    const absoluteRoute = new URL(
      normalizedRoute.startsWith('/') ? normalizedRoute : `/${normalizedRoute}`,
      window.location.origin,
    ).href;

    const payload = {
      relativeRoute: routeLink,
      normalizedRoute,
      absoluteRoute,
    };

    this.storePendingNavigation(payload);

    // Visible immediately before navigation (may be cleared by browser on route change).
    console.warn('[TextRowsTile] Learn More navigation (pre-nav)', payload);
  }

  private logPendingNavigation() {
    if (typeof window === 'undefined') {
      return;
    }

    const pendingLog = sessionStorage.getItem(this.navLogStorageKey);
    if (!pendingLog) {
      return;
    }

    try {
      const payload = JSON.parse(pendingLog);
      console.warn('[TextRowsTile] Learn More navigation (post-nav)', payload);
    } catch {
      console.warn('[TextRowsTile] Unable to parse pending navigation log payload');
    } finally {
      sessionStorage.removeItem(this.navLogStorageKey);
    }
  }

  private storePendingNavigation(payload: {
    relativeRoute?: string;
    normalizedRoute: string;
    absoluteRoute: string;
  }) {
    if (typeof window === 'undefined') {
      return;
    }

    sessionStorage.setItem(this.navLogStorageKey, JSON.stringify(payload));
  }
}
